import React, { useEffect, useRef } from "react"; // Import useRef
import styles from "./StandingOrderCardInfoDropdown.module.css";
import PropTypes from "prop-types";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAlignRight,
  faAlignLeft,
  faAlignCenter,
} from "@fortawesome/free-solid-svg-icons";
import ChevronButton from "NewsAdmin/Shared/ChevronButton";
import SpanInput from "NewsAdmin/Shared/SpanInput";
import SelectNumberInput from "NewsAdmin/Shared/SelectNumberInput";
import ShapeButton from "NewsAdmin/Shared/ShapeButton";
import StandingOrderRadioButtonGroup from "../StandingOrderRadioButtonGroup";
import StandingOrderIcon from "../StandingOrderIcon";
import SelectColorInput from "../SelectColorInput";
import ButtonStyledRadioButtonGroup from "../ButtonStyledRadioButtonGroup";
import { SwitchTransition, CSSTransition } from "react-transition-group";

/**
 * Interactive component.
 *  Dropdown that allows a Standing Order feed’s card information to be modified or created.
 */
export function StandingOrderCardInfoDropdown({
  cardInformation,
  setCardInformation,
  isOpen,
  onClick,
}) {
  const transitionRef = useRef(null); // Insantiate ref for transitions

  const [activeColorPicker, setActiveColorPicker] = useState(null);

  const [info, setInfo] = useState(cardInformation.data.card);
  useEffect(() => {
    // console.log(info);
  }, [info]);

  // Destructure data constants
  const { header = {}, details = {}, gradient = {}, image = {} } = info || {};

  // Make sure stops always exists and is an array
  const gradientStops = info?.gradient?.stops ?? [];

  const angleOptions = Array.from({ length: 360 / 15 + 1 }, (_, i) => i * 15);
  const angleOptionsString = angleOptions.map(String);

  const [customColors, setCustomColors] = useState({
    preTitleCustomColor: "#fff",
    headerTitleCustomColor: "#fff",
    subTitleCustomColor: "#fff",
    skuCustomColor: "#fff",
    creatorCustomColor: "#fff",
    detailsTitleCustomColor: "#fff",
    iconTitleCustomColor: "#fff",
    firstGradientCustomColor: "#6998AD",
    secondGradientCustomColor: "#fff",
  });

  // Gradient colors
  const [buttons, setButtons] = useState([
    ...gradientStops.map((stop) => ({
      customColor: stop.color || stop,
      currentColor: stop.color || stop,
    })),
    // {
    //   customColor: customColors.firstGradientCustomColor,
    //   currentColor: customColors.firstGradientCustomColor,
    // },
    // {
    //   customColor: customColors.secondGradientCustomColor,
    //   currentColor: customColors.secondGradientCustomColor,
    // },
  ]);

  // Ref for color pickers
  const colorPickerRefs = {
    preTitleColor: React.createRef(),
    headerTitleColor: React.createRef(),
    subTitleColor: React.createRef(),
    skuColor: React.createRef(),
    creatorColor: React.createRef(),
    detailsTitleColor: React.createRef(),
    iconTitleColor: React.createRef(),
    ...buttons.map((_, i) => ({ ["gradientColor-" + i]: React.createRef() })),
  };

  const [gradientColorRefs, setGradientColorRefs] = useState(
    buttons.map(() => null)
  );

  useEffect(() => {
    function handleOutsideClick(event) {

      // --- check gradient refs ---
      const clickedInsideGradient = gradientColorRefs.some(
        (ref) => ref && ref.contains(event.target)
      );

      // --- check other pooled refs ---
      let clickedInsideAny = false;
      for (let [key, ref] of Object.entries(colorPickerRefs)) {
        if (ref?.current && ref.current.contains(event.target)) {
          clickedInsideAny = true;
          break;
        }
      }

      // --- close only if truly outside everything ---
      if (
        !clickedInsideGradient &&
        !clickedInsideAny
      ) {
        setActiveColorPicker(null);
      }
    }

    document.addEventListener("mouseup", handleOutsideClick);
    return () => {
      document.removeEventListener("mouseup", handleOutsideClick);
    };
  }, [
    gradientColorRefs,
    colorPickerRefs,
  ]);

  // Adding new gradient color to the array.
  const addNewButton = () => {
    const newButton = {
      customColor: "#fff",
      currentColor: "#FFF",
    };
    setButtons([...buttons, newButton]);
  };

  // Delete gradient color from the array.
  const deleteButton = (index) => {
    if (buttons.length <= 2) {
      // Don't allow fewer than 2 gradient colors
      return;
    }

    // Update buttons for UI
    const newButtons = [...buttons];
    newButtons.splice(index, 1);
    setButtons(newButtons);

    // Update the *real* info.gradient.stops array too
    let newGradientStops;

    if (gradientStops.length > 0) {
      newGradientStops = gradientStops.map((stop) => ({ ...stop })); // defensive copy
    } else {
      newGradientStops = [{ color: "#6998AD" }, { color: "#FFF" }];
    }

    // Remove the clicked index from gradient stops
    newGradientStops.splice(index, 1);

    const newInfo = {
      ...info,
      gradient: { ...info.gradient, stops: newGradientStops },
    };

    setInfo(newInfo);
    setCardInformation({ ...cardInformation, data: { ...cardInformation.data, card: newInfo } });
  };

  // Handles custom color in gradient array
  const handleCustomColorChange = (index, colorValue) => {
    const newButtons = [...buttons];
    newButtons[index].customColor = colorValue;
    setButtons(newButtons);

    // const newGradientColors = [...info.gradient.stops.map((stops) => {
    //   return stops.color;
    // })];
    // newGradientColors[index] = colorValue;
    // handleGradientColorChange("gradient", "gradientColors", newGradientColors);
  };

  // Handles current color in gradient array
  const handleCurrentColorChange = (index, e) => {
    const newButtons = [...buttons];
    newButtons[index].currentColor = e.target.style.backgroundColor;
    setButtons(newButtons);

    let newGradientStops;

    if (gradientStops.length > 0) {
      newGradientStops = gradientStops.map((stop) =>
        stop?.color ? { ...stop } : stop
      );
    } else {
      // fallback to two safe default colors if empty
      newGradientStops = [{ color: "#6998AD" }, { color: "#FFF" }];
    }

    // now, update the clicked index
    newGradientStops[index] = {
      color: e.target.style.backgroundColor,
    };

    const newInfo = {
      ...info,
      gradient: { ...info.gradient, stops: newGradientStops },
    };
    setInfo(newInfo);
    setCardInformation({
      ...cardInformation,
      data: { ...cardInformation.data, card: newInfo },
    });
  };

  // Handles select color input in gradientColors array
  const handleGradientColorChange = (category, field, value) => {
    setCardInformation((prevData) => ({
      ...prevData,
      [category]: {
        ...prevData[category],
        gradientColors: value,
      },
    }));
  };

  //Function to get the input values
  const handleInputChange = (category, field, text, color, value) => {
    let newInfo = { ...info };

    text
      ? (newInfo[category][field].text = value)
      : color
      ? (newInfo[category][field].color = value)
      : (newInfo[category][field] = value);

    setInfo(newInfo);
    setCardInformation({ ...cardInformation, data: { ...cardInformation.data, card: newInfo } });
  };

  // Handles select color input in gradient array
  const handleIsOpenChange = (index, isOpen) => {
    const pickerId = "gradientColor-" + index;
    setActiveColorPicker(isOpen ? pickerId : null);
  };

  return (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={isOpen ? "SOdropdown" : "button"}
        nodeRef={transitionRef} // StrictMode integrity
        timeout={300}
        classNames={{
          enter: styles["fade-enter"],
          exit: styles["fade-exit"],
          enterActive: styles["fade-enter-active"],
          exitActive: styles["fade-exit-active"],
        }}
      >
        {/* Wrap everything in a refable node */}
        <div className={styles.container} ref={transitionRef}>
          {!isOpen ? (
            <section
              className={styles["outside-container"]}
              onClick={() => onClick(isOpen ? "close" : "info")}
            >
              <div className={styles["card-dropdown"]}>
                <h3 className={styles.title}>Card Information:</h3>
                <ChevronButton
                  orientation={isOpen ? "down" : "right"}
                  onClick={() => onClick(isOpen ? "close" : "info")}
                  className={styles.chevronIcon}
                />
              </div>

              <div className={styles["outside-content"]}>
                {/* Standing order Icon component wasn't even needed */}
                <StandingOrderIcon
                  iconType={info?.image.path}
                  iconSize="72px"
                />

                <div className={styles["details-container"]}>
                  <div className={styles["left-details"]}>
                    <p className={styles.text}>{header?.topTitle?.text}</p>

                    <p className={styles.text}>{header?.title?.text}</p>
                    <p className={styles.text}>{header?.subTitle?.text}</p>
                  </div>

                  <div className={styles["right-details"]}>
                    <p className={styles.text}>{details?.sku?.text}</p>
                    <p className={styles.text}>{details?.creator?.text}</p>
                    <p className={styles.text}>{details?.title?.text}</p>
                  </div>
                </div>
              </div>
              <hr className={styles.hr} />
            </section>
          ) : (
            <section>
              <div className={styles["inside-content"]}>
                <h3 className={styles.title}>Card Information:</h3>
                <ChevronButton
                  orientation={isOpen ? "down" : "right"}
                  onClick={() => onClick(isOpen ? "close" : "info")}
                  className={styles.chevronIcon}
                />
              </div>
              <div className={styles["header-container"]}>
                <h5 className={styles.header}>Header</h5>
                <div className={styles["input-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={header?.topTitle?.text}
                      setCurrentValue={(e) =>
                        handleInputChange(
                          "header",
                          "topTitle",
                          true,
                          false,
                          e.target.value
                        )
                      }
                      inputType={"text"}
                      inputLabel={"Pre-Title"}
                      placeholder={header?.topTitle?.text && "* Pre-Title *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["preTitleColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "preTitleColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "preTitleColor"
                            ? null
                            : "preTitleColor"
                        );
                      }}
                      customColor={customColors.preTitleCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          preTitleCustomColor: value,
                        })
                      }
                      currentColor={header?.topTitle?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "header",
                          "topTitle",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>

                <div className={styles["input-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={header?.title?.text}
                      setCurrentValue={(e) => {
                        handleInputChange(
                          "header",
                          "title",
                          true,
                          false,
                          e.target.value
                        );
                      }}
                      inputType={"text"}
                      inputLabel={"Title"}
                      placeholder={"* New Standing Order Title *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["headerTitleColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "headerTitleColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "headerTitleColor"
                            ? null
                            : "headerTitleColor"
                        );
                      }}
                      customColor={customColors.headerTitleCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          headerTitleCustomColor: value,
                        })
                      }
                      currentColor={header?.title?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "header",
                          "title",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>

                <div className={styles["input-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={header?.subTitle?.text}
                      setCurrentValue={(e) => {
                        handleInputChange(
                          "header",
                          "subTitle",
                          true,
                          false,
                          e.target.value
                        );
                      }}
                      inputType={"text"}
                      inputLabel={"Sub Title"}
                      placeholder={"* New Sub Title *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["subTitleColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "subTitleColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "subTitleColor"
                            ? null
                            : "subTitleColor"
                        );
                      }}
                      customColor={customColors.subTitleCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          subTitleCustomColor: value,
                        })
                      }
                      currentColor={header?.subTitle?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "header",
                          "subTitle",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>
              </div>

              <div className={styles["container-margin"]}>
                <h5 className={styles.header}>Standing Order Details</h5>
                <div className={styles["input-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={details?.sku?.text}
                      setCurrentValue={(e) => {
                        handleInputChange(
                          "details",
                          "sku",
                          true,
                          false,
                          e.target.value
                        );
                      }}
                      inputType={"text"}
                      inputLabel={"SKU"}
                      placeholder={"* WW### *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["skuColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "skuColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "skuColor" ? null : "skuColor"
                        );
                      }}
                      customColor={customColors.skuCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          skuCustomColor: value,
                        })
                      }
                      currentColor={details?.sku?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "details",
                          "sku",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>

                <div className={styles["input-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={details?.creator?.text}
                      setCurrentValue={(e) => {
                        handleInputChange(
                          "details",
                          "creator",
                          true,
                          false,
                          e.target.value
                        );
                      }}
                      inputType={"text"}
                      inputLabel={"Creator"}
                      placeholder={"* Creator's Name Here *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["creatorColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "creatorColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "creatorColor"
                            ? null
                            : "creatorColor"
                        );
                      }}
                      customColor={customColors.creatorCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          creatorCustomColor: value,
                        })
                      }
                      currentColor={details?.creator?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "details",
                          "creator",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>

                <div className={styles["input-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={details?.title?.text}
                      setCurrentValue={(e) => {
                        handleInputChange(
                          "details",
                          "title",
                          true,
                          false,
                          e.target.value
                        );
                      }}
                      inputType={"text"}
                      inputLabel={"Title"}
                      placeholder={"* Type of Standing Order *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["detailsTitleColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "detailsTitleColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "detailsTitleColor"
                            ? null
                            : "detailsTitleColor"
                        );
                      }}
                      customColor={customColors.detailsTitleCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          detailsTitleCustomColor: value,
                        })
                      }
                      currentColor={details?.title?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "details",
                          "title",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>
              </div>

              <div className={styles["container-margin"]}>
                <h5 className={styles.header}>Icon</h5>

                <StandingOrderRadioButtonGroup
                  currentIcon={image?.path}
                  setCurrentIcon={(e) => {
                    handleInputChange(
                      "image",
                      "path",
                      false,
                      false,
                      e.target.title
                    );
                  }}
                />

                <div className={styles["icon-container"]}>
                  <div className={styles["span-input"]}>
                    <SpanInput
                      currentValue={image?.subText?.text}
                      setCurrentValue={(e) => {
                        handleInputChange(
                          "image",
                          "subText",
                          true,
                          false,
                          e.target.value
                        );
                      }}
                      inputType={"text"}
                      inputLabel={"Icon Text"}
                      placeholder={"* Icon Title *"}
                    />
                  </div>

                  <div
                    ref={colorPickerRefs["iconTitleColor"]}
                    className={styles.color}
                  >
                    <SelectColorInput
                      isOpen={activeColorPicker === "iconTitleColor"}
                      setIsOpen={() => {
                        setActiveColorPicker(
                          activeColorPicker === "iconTitleColor"
                            ? null
                            : "iconTitleColor"
                        );
                      }}
                      customColor={customColors.iconTitleCustomColor}
                      setCustomColor={(value) =>
                        setCustomColors({
                          ...customColors,
                          iconTitleCustomColor: value,
                        })
                      }
                      currentColor={image?.subText?.color}
                      setCurrentColor={(e) => {
                        handleInputChange(
                          "image",
                          "subText",
                          false,
                          true,
                          e.target.style.backgroundColor
                        );
                        setActiveColorPicker(null);
                      }}
                      labelText={"Color"}
                      className={styles.textColor}
                    />
                  </div>
                </div>
              </div>

              <div className={styles["button-styled"]}>
                <ButtonStyledRadioButtonGroup
                  options={[
                    {
                      value: "left",
                      buttonContent: <FontAwesomeIcon icon={faAlignLeft} />,
                    },
                    {
                      value: "center",
                      buttonContent: <FontAwesomeIcon icon={faAlignCenter} />,
                    },
                    {
                      value: "right",
                      buttonContent: <FontAwesomeIcon icon={faAlignRight} />,
                    },
                  ]}
                  currentlySelected={image?.alignment}
                  setCurrentlySelected={(e) => {
                    handleInputChange(
                      "image",
                      "alignment",
                      false,
                      false,
                      e.target.value
                    );
                  }}
                />
              </div>

              {/* GRADIENT COLOR */}
              <div className={styles["container-margin"]}>
                <h5 className={styles.header}>
                  Gradient (must have at least two colors)
                </h5>
                <div className={styles["colors-container"]}>
                  <SelectNumberInput
                    labelText={"Angle"}
                    value={String(gradient?.angle)} // Expects string, give it string
                    onSelect={(e) => {
                      handleInputChange(
                        "gradient",
                        "angle",
                        false,
                        false,
                        e.target.value
                      );
                    }}
                    numberOptions={angleOptionsString}
                  />

                  <div className={styles["add-color"]}>
                    {buttons.map((button, index) => (
                      <div className={styles["colors-container"]} key={index}>
                        <span className={styles["shape-button"]}>
                          <ShapeButton
                            shape={"round"}
                            onClick={() => deleteButton(index)}
                          />
                        </span>

                        <div
                          ref={colorPickerRefs["gradientColor-" + index]}
                          className={styles["gradient-colors"]}
                        >
                          <SelectColorInput
                            setRef={(ref) => {
                              setGradientColorRefs((prevRefs) => {
                                const updatedRefs = [...prevRefs];
                                updatedRefs[index] = ref;
                                return updatedRefs;
                              });
                            }}
                            isOpen={
                              activeColorPicker === "gradientColor-" + index
                            }
                            setIsOpen={() => {
                              handleIsOpenChange(
                                index,
                                activeColorPicker !== "gradientColor-" + index
                              );
                            }}
                            customColor={button.customColor}
                            setCustomColor={(value) => {
                              handleCustomColorChange(index, value);
                              setActiveColorPicker(null);
                            }}
                            currentColor={button.currentColor}
                            setCurrentColor={(e) => {
                              handleCurrentColorChange(index, e);
                            }}
                            className={styles.secondColor}
                            onMouseDown={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                    ))}

                    <span className={styles["shape-button"]}>
                      <ShapeButton shape={"square"} onClick={addNewButton} />
                    </span>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
}

// Updated to correct PropTypes as expected from "live" data/endpoint
StandingOrderCardInfoDropdown.propTypes = {
  /**
   * Object that contains a Standing Order feed item’s card information.
   */
  // Update to use correct shape
  cardInformation: PropTypes.shape({
    card: PropTypes.shape({
      header: PropTypes.shape({
        topTitle: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        title: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        subTitle: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
      }),
      details: PropTypes.shape({
        sku: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        creator: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        title: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
      }),
      image: PropTypes.shape({
        path: PropTypes.string,
        alignment: PropTypes.oneOf(["left", "center", "right"]),
        subText: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
      }),
      gradient: PropTypes.shape({
        angle: PropTypes.number,
        stops: PropTypes.arrayOf(
          PropTypes.shape({
            color: PropTypes.string,
          })
        ),
      }),
    }),
  }),
  /**
   * Function that allows the cardInformation to be reassigned.
   */
  setCardInformation: PropTypes.func,
  /**
   * Boolean that determines if the dropdown is expanded.
   */
  isOpen: PropTypes.bool,
  /**
   * Function to set the current dropdown open.
   */
  onClick: PropTypes.func, // (you named it "onClick" not "setCurrentDropdown")
};

StandingOrderCardInfoDropdown.defaultProps = {
  // Update to use correct shape
  cardInformation: PropTypes.shape({
    card: PropTypes.shape({
      header: PropTypes.shape({
        topTitle: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        title: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        subTitle: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
      }),
      details: PropTypes.shape({
        sku: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        creator: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
        title: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
      }),
      image: PropTypes.shape({
        path: PropTypes.string,
        alignment: PropTypes.oneOf(["left", "center", "right"]),
        subText: PropTypes.shape({
          text: PropTypes.string,
          color: PropTypes.string,
        }),
      }),
      gradient: PropTypes.shape({
        angle: PropTypes.number,
        stops: PropTypes.arrayOf(
          PropTypes.shape({
            color: PropTypes.string,
          })
        ),
      }),
    }),
  }),
};
